import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, FileDown, Info, FileSpreadsheet, HandCoins, Plus, Layers } from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/Common";
import { DisbursementProjectField } from "@/components/DisbursementProjectField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { formatTanggal } from "@/lib/format";
import { toast } from "sonner";

function monthStart() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function today() { return new Date().toISOString().slice(0, 10); }

export default function InvestorLaporan() {
  const { projects = [], projectId, projectParam } = useProjects();
  const projName = projectId !== "all" ? projects.find((p) => p.id === projectId)?.name : null;
  const { canManage, isSuperadmin } = useAuth();
  const [investorId, setInvestorId] = useState("");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const { data: investors = [] } = useQuery({ queryKey: ["investors", projectId], queryFn: async () => (await api.get("/investors", { params: projectParam })).data });
  useEffect(() => {
    if (investorId && !investors.some((i) => i.id === investorId)) { setInvestorId(""); setReport(null); }
  }, [investors, investorId]);
  useEffect(() => { setReport(null); }, [projectId]);

  const params = () => ({ investor_id: investorId, date_from: from, date_to: to, ...(projectId !== "all" ? { project_id: projectId } : {}) });

  const generate = async () => {
    if (!investorId) { toast.error("Pilih investor terlebih dahulu."); return; }
    setLoading(true);
    try { setReport((await api.post("/reports/investor", params())).data); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    if (!investorId) { toast.error("Pilih investor terlebih dahulu."); return; }
    try {
      const res = await api.post("/reports/investor/pdf", params(), { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    if (!investorId) { toast.error("Pilih investor terlebih dahulu."); return; }
    try {
      const res = await api.post("/reports/investor/export.csv", params(), { responseType: "blob" });
      downloadBlob(res.data, "laporan-investor.csv");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadCombined = async () => {
    if (!investorId) { toast.error("Pilih investor terlebih dahulu."); return; }
    try {
      const res = await api.post("/reports/investor/combined-pdf", { investor_id: investorId, date_from: from, date_to: to }, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const selInv = investors.find((i) => i.id === investorId);

  const exportInvestors = async (fmt) => {
    try {
      const res = await api.get(`/investors/export.${fmt}`, { params: projectParam, responseType: "blob" });
      downloadBlob(res.data, `daftar-investor.${fmt}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Investor & Laporan" description={`Laporan hak investor (80%) atas komponen sewa lunas pada rentang tanggal pilihan${projName ? ` — khusus proyek ${projName}` : isSuperadmin ? " — semua proyek" : " — proyek Anda"}.`}
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportInvestors("csv")} data-testid="investor-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> Daftar Investor CSV</Button>
          <Button variant="outline" onClick={() => exportInvestors("xlsx")} data-testid="investor-export-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" /> Daftar Investor Excel</Button>
        </div>} />

      <SectionCard testid="report-filter">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs">Investor{projName ? ` (${projName})` : ""}</Label>
            <Select value={investorId} onValueChange={setInvestorId}>
              <SelectTrigger data-testid="report-investor-select"><SelectValue placeholder="Pilih investor" /></SelectTrigger>
              <SelectContent>{investors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Dari (tgl pelunasan)</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" /></div>
          <div className="flex items-end gap-2">
            <Button onClick={generate} disabled={loading} className="flex-1" data-testid="report-generate">Tampilkan</Button>
            <Button onClick={downloadPdf} variant="outline" data-testid="report-pdf" title="Unduh PDF"><FileDown className="h-4 w-4" /></Button>
            <Button onClick={exportCsv} variant="outline" data-testid="report-csv" title="Ekspor CSV"><FileSpreadsheet className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-border px-3 py-2">
          <span className="text-xs text-muted-foreground" data-testid="report-combined-hint">
            {selInv ? `${selInv.name} terdaftar di ${(selInv.project_ids || []).length} proyek. ` : ""}PDF gabungan memuat semua proyek investor dalam satu dokumen (penghuni, lama sewa, sewa, potongan 20%, bersih 80%, dan pencairan per proyek).
          </span>
          <Button size="sm" variant="outline" onClick={downloadCombined} disabled={!investorId} data-testid="report-combined-pdf"><Layers className="mr-1.5 h-4 w-4" /> PDF Gabungan Semua Proyek</Button>
        </div>
      </SectionCard>

      {loading ? <Loading /> : report && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SectionCard title="Total Sewa Lunas"><div className="text-2xl font-extrabold"><Money value={report.rent_total} /></div></SectionCard>
            <SectionCard title="Total Potongan Perawatan (20%)"><div className="text-2xl font-extrabold"><Money value={report.care_total} /></div></SectionCard>
            <SectionCard title="Total Bersih Investor (80%)"><div className="text-2xl font-extrabold text-primary"><Money value={report.investor_total} /></div></SectionCard>
          </div>

          {report.disbursed_total !== null && report.disbursed_total !== undefined && (
            <DisbursementPanel report={report} investor={investors.find((i) => i.id === investorId)} canRecord={canManage} fixedProjectId={projectId !== "all" ? projectId : null} onChanged={generate} />
          )}

          {report.houses.length === 0 ? (
            <EmptyState title="Tidak ada transaksi" description="Tidak ada komponen sewa lunas pada rentang & lingkup ini." icon={TrendingUp} />
          ) : report.houses.map((h) => (
            <SectionCard key={h.house_id} title={h.house_name} description={h.project_name} testid={`report-house-${h.house_id}`}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kamar</TableHead>
                      <TableHead>Penghuni</TableHead>
                      <TableHead>Paket</TableHead>
                      <TableHead>Periode</TableHead>
                      <TableHead className="text-right">Dasar Sewa</TableHead>
                      <TableHead className="text-right">Perawatan 20%</TableHead>
                      <TableHead className="text-right">Bersih Investor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {h.items.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{it.room_number}</TableCell>
                        <TableCell>{it.resident_name}</TableCell>
                        <TableCell>{it.package_months} bln</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{it.period_label}</TableCell>
                        <TableCell className="text-right"><Money value={it.rent_price} /></TableCell>
                        <TableCell className="text-right text-muted-foreground"><Money value={it.care} /></TableCell>
                        <TableCell className="text-right font-semibold"><Money value={it.investor} /></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-accent/40 font-semibold">
                      <TableCell colSpan={4}>Subtotal {h.house_name}</TableCell>
                      <TableCell className="text-right"><Money value={h.rent_total} /></TableCell>
                      <TableCell className="text-right"><Money value={h.care_total} /></TableCell>
                      <TableCell className="text-right text-primary"><Money value={h.investor_total} /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          ))}

          <div className="flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Laporan tidak memuat foto KTP, NIK, kontak wali, IPL, DP belum lunas, maupun DP hangus. Ini perhitungan hak berdasarkan aturan bisnis, bukan bukti dana telah ditransfer ke investor.</span>
          </div>
        </div>
      )}

      {!report && !loading && <EmptyState title="Pilih investor & rentang tanggal" description="Lalu klik Tampilkan untuk membuat laporan." icon={TrendingUp} />}
    </div>
  );
}

function DisbursementPanel({ report, investor, canRecord, fixedProjectId, onChanged }) {
  const [open, setOpen] = useState(false);
  const blank = { project_id: "", amount: "", date: new Date().toISOString().slice(0, 10), method: "transfer", reference: "", note: "" };
  const [f, setF] = useState(blank);
  const save = async () => {
    if (!fixedProjectId && (investor?.project_ids || []).length > 1 && !f.project_id) { toast.error("Pilih proyek."); return; }
    if (!Number(f.amount)) { toast.error("Nominal pencairan wajib diisi."); return; }
    try {
      await api.post("/investor-disbursements", { investor_id: report.investor_id, project_id: fixedProjectId || f.project_id || undefined, amount: Number(f.amount), date: f.date, method: f.method, reference: f.reference, note: f.note });
      toast.success("Pencairan tercatat."); setOpen(false); setF(blank); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const over = (report.outstanding || 0) > 0;
  return (
    <SectionCard title="Pencairan ke Investor (Realisasi)" description="Perbandingan hak investor dengan dana yang benar-benar sudah ditransfer." testid="disbursement-panel"
      actions={canRecord && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-disbursement-button"><HandCoins className="mr-1.5 h-4 w-4" /> Catat Pencairan</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Catat Pencairan ke Investor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <DisbursementProjectField investor={investor} value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })} fixedProjectId={fixedProjectId} />
              <div><Label>Nominal</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="disbursement-amount" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="disbursement-date" /></div>
                <div><Label>Metode</Label>
                  <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger data-testid="disbursement-method"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Referensi</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="disbursement-reference" /></div>
              <div><Label>Catatan</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="disbursement-note" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="disbursement-save">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">Hak Investor (periode)</div><div className="text-lg font-bold"><Money value={report.investor_total} /></div></div>
        <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">Sudah Dicairkan (tercatat)</div><div className="text-lg font-bold"><Money value={report.disbursed_total || 0} /></div></div>
        <div className="rounded-lg border p-3" style={{ borderColor: over ? "#f7cfc2" : "#bbf7d0", backgroundColor: over ? "#fdf2ee" : "#f0fdf4" }}>
          <div className="text-xs text-muted-foreground">Selisih (belum dicairkan)</div><div className="text-lg font-bold"><Money value={report.outstanding || 0} /></div></div>
      </div>
      {report.disbursements?.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {report.disbursements.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
              <span>{formatTanggal(d.date)} · {d.method_label || d.method} · {d.reference || "tanpa referensi"}</span>
              <Money value={d.amount} />
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Laporan adalah perhitungan hak; pencairan di atas adalah realisasi transfer yang dicatat pengelola.</p>
    </SectionCard>
  );
}
